import { useSelector } from 'react-redux';

import { RootState } from '../redux/rootReducer';
import { SheetDb } from '../redux/sheetDbSlice';
import useSheetTreeDbsByBook from './useSheetTreeDbsByBook';
import { FoldedHash } from '../components/BookTree';

export type SheetNodeType = SheetDb & {
  parentId: string;
  deepness: number;
  order: number;
  appearanceOrder: number;
  descendantIds: string[];
  shownLength: number;
  treeId: string;
};
export type SheetListHash = { [key in string]: SheetNodeType; }

const useCurrentTreeHash = (bookId: string | null, foldedSheets: FoldedHash): SheetListHash => {
  const sheetDb = useSelector((state: RootState) => state.sheetDb);
  const [sheetTreeIds, sheetTrees] = useSheetTreeDbsByBook(bookId || undefined);

  if (!bookId || Object.keys(sheetDb.sheets).length === 0) return {};

  const sheetIds = sheetTreeIds.map((id, ix) => sheetTrees[ix].sheetId);
  const sheetList: SheetListHash = {};
  sheetIds.forEach((id, ix) => {
    const sheet = sheetDb.sheets[id];
    const sheetTreeId = sheetTreeIds[ix];
    const sheetTree = sheetTrees[ix];
    if (!sheet || !sheetTreeId || !sheetTree) return;
    sheetList[id] = {
      ...sheet,
      parentId: sheetTree.parentSheetId ? sheetTree.parentSheetId : sheetTree.bookId,
      deepness: 0,
      order: sheetTree.order,
      appearanceOrder: sheetTree.order,
      descendantIds: [],
      shownLength: 1,
      treeId: sheetTreeId
    };
  })

  const rootIds = sheetIds.filter(id => sheetList[id].parentId === bookId).sort((a, b) => sheetList[a].order - sheetList[b].order);
  type SubListIdsHash = { [key in string]: string[]; }
  const subListIds: SubListIdsHash = {};
  sheetIds.forEach(id => {
    if (rootIds.includes(id)) return;
    const sheet = sheetList[id];
    if (!subListIds[sheet.parentId]) subListIds[sheet.parentId] = [id];
    else subListIds[sheet.parentId].push(id);
  })
  const parentIds = Object.keys(subListIds);
  parentIds.forEach(id => subListIds[id].sort((a, b) => sheetList[a].order - sheetList[b].order))

  let remainIds: string[] = [];
  const getSortedIds = (ids: string[], deepness: number) => {
    remainIds = [];
    ids.forEach(id => {
      const preIx = rootIds.indexOf(id);
      const subSheetIds = subListIds[id];
      const sheet = sheetList[id];
      if (preIx >= 0 && sheet.deepness === deepness - 1) {
        rootIds.splice(preIx + 1, 0, ...subSheetIds);
        subSheetIds.forEach(subId => sheetList[subId].deepness = deepness)
      }
      else remainIds.push(id);
    })
    if (remainIds.length >= 1) {
      getSortedIds(remainIds, deepness + 1);
    }
  }
  getSortedIds(parentIds, 1);

  rootIds.forEach((id, ix) => {
    const sheet = sheetList[id];
    for (let i = ix + 1; i < rootIds.length; i++) {
      const targetId = rootIds[i];
      const target = sheetList[targetId];
      if (target.deepness <= sheet.deepness) break;
      sheet.descendantIds.push(targetId);
    }
  })

  let appearanceOrderValue = 0;
  rootIds.forEach(id => {
    const sheet = sheetList[id];

    const foldedAncestorIds = parentIds.filter(targetId => {
      return Object.keys(foldedSheets).includes(targetId) && sheetList[targetId].descendantIds.includes(id);
    });
    if (foldedAncestorIds.length > 0) {
      sheetList[id].appearanceOrder = appearanceOrderValue - 1;
      sheetList[id].shownLength = 0;
      return;
    }

    const foldedDescendantLength = sheet.descendantIds.length === 0 ? 0 : sheet.descendantIds.map(targetId => {
      const target = sheetList[targetId];
      const foldedData = foldedSheets[targetId];
      const isDescendantFolded = Object.keys(foldedSheets).some(a => target.descendantIds.includes(a));
      if (foldedData && !isDescendantFolded) return foldedData.descendantLength;
      else if (foldedData && isDescendantFolded) return foldedData.childrenLength;
      else return 0;
    }).reduce((sum, value) => sum + value);
    const descendantLength = Object.keys(foldedSheets).includes(id) ? 0 : sheet.descendantIds.length - foldedDescendantLength;

    sheetList[id].appearanceOrder = appearanceOrderValue;
    sheetList[id].shownLength += descendantLength;
    appearanceOrderValue++;
  })

  return sheetList;
}

export default useCurrentTreeHash;
